<template>
  <div>
    <header-component />

    <partner-pin-location-modal
      ref="joinPinModal"
      modal-id="JoinPartnerPinModal"
      map-element-id="join-partner-pin-map"
      @confirmed="onJoinPinConfirmed"
    />

    <section class="w-100 d-inline-block join-wrap">
      <div class="join-bg join-bg--left" aria-hidden="true"></div>
      <div class="join-bg join-bg--right" aria-hidden="true"></div>

      <div class="container autoContent">
        <div class="row justify-content-center">
          <div class="col-xl-5 col-lg-6 col-md-8 col-11">
            <div class="text-center mb-2">
              <div class="join-page-title filson-black-family text-uppercase text-black">
                {{ stepHeading }}
              </div>
            </div>

            <div class="w-100 d-inline-block join-card bg-white rounded-3 shadow-sm">
              <div class="text-center">
                <div v-if="step === 3 && isGroomer" class="join-subtitle inter-bold-family text-uppercase text-black">
                  {{ groomerStep3Kicker }}
                </div>
                <div v-else class="join-subtitle inter-bold-family text-uppercase text-black">
                  {{ stepSubheading }}
                </div>

                <div v-if="step === 3 && isGroomer" class="groomer-s3-tagline inter-regular-family text-black text-full-11">
                  {{ groomerStep3Tagline }}
                </div>
              </div>

              <div class="join-steps" aria-label="Progress steps">
                <span class="join-step" :class="stepCircleClass(1)">
                  <span v-if="step > 1" aria-hidden="true">✓</span>
                  <span v-else>1</span>
                </span>
                <span class="join-step join-step--line" aria-hidden="true"></span>
                <span class="join-step" :class="stepCircleClass(2)">
                  <span v-if="step > 2" aria-hidden="true">✓</span>
                  <span v-else>2</span>
                </span>
                <span class="join-step join-step--line" aria-hidden="true"></span>
                <span class="join-step" :class="stepCircleClass(3)">3</span>
              </div>

              <div v-if="step === 1" class="w-100 d-inline-block mt-3">
                <div class="inter-regular-family text-black text-size-12 text-full-12 mb-2">
                  {{ joinQuestion }}
                </div>

                <div class="join-options">
                  <button
                    v-for="opt in joinOptions"
                    :key="opt.value"
                    type="button"
                    class="join-pill"
                    :class="{ 'join-pill--active': form.joinType === opt.value }"
                    @click="onJoinTypeClick(opt.value)"
                  >
                    <span class="join-pill-icon" aria-hidden="true">
                      <img
                        class="join-pill-icon-img"
                        :src="joinOptionIconSrc(opt.value)"
                        alt=""
                      >
                    </span>
                    <span class="inter-regular-family text-uppercase text-full-11 join-pill-label">{{ opt.label }}</span>
                  </button>
                </div>
              </div>

              <form v-if="step === 1" class="w-100 d-inline-block mt-3" @submit.prevent="goNext">
                <div class="w-100 d-inline-block mb-2">
                  <label class="join-label inter-regular-family text-black text-full-11">
                    Country<span class="join-req" aria-hidden="true">*</span>
                  </label>
                  <select v-model="form.countrySlug" class="join-input join-input--select" required>
                    <option value="" disabled>Select country</option>
                    <option v-for="c in activeCountries" :key="c.slug" :value="c.slug">
                      {{ c.name }}
                    </option>
                  </select>
                </div>

                <div class="w-100 d-inline-block mb-2">
                  <label class="join-label inter-regular-family text-black text-full-11">
                    {{ labels.firstName }}<span class="join-req" aria-hidden="true">*</span>
                  </label>
                  <input
                    v-model.trim="form.firstName"
                    class="join-input"
                    type="text"
                    :placeholder="placeholders.firstName"
                    required
                  >
                </div>

                <div class="w-100 d-inline-block mb-2">
                  <label class="join-label inter-regular-family text-black text-full-11">
                    {{ labels.lastName }}<span class="join-req" aria-hidden="true">*</span>
                  </label>
                  <input
                    v-model.trim="form.lastName"
                    class="join-input"
                    type="text"
                    :placeholder="placeholders.lastName"
                    required
                  >
                </div>

                <div class="w-100 d-inline-block mb-2">
                  <label class="join-label inter-regular-family text-black text-full-11">
                    {{ labels.email }}<span class="join-req" aria-hidden="true">*</span>
                  </label>
                  <input
                    v-model.trim="form.email"
                    class="join-input"
                    :class="{ 'join-input--invalid': Boolean(step1EmailError) }"
                    type="email"
                    :placeholder="placeholders.email"
                    autocomplete="email"
                    required
                    :aria-invalid="step1EmailError ? 'true' : 'false'"
                    :aria-describedby="step1EmailError ? 'join-email-error' : undefined"
                  >
                  <div v-if="step1EmailError" id="join-email-error" class="join-field-error inter-regular-family text-full-11" role="alert">
                    {{ step1EmailError }}
                  </div>
                </div>

                <div class="w-100 d-inline-block mb-2">
                  <label class="join-label inter-regular-family text-black text-full-11">
                    {{ labels.phone }}<span class="join-req" aria-hidden="true">*</span>
                  </label>
                  <input
                    v-model.trim="form.phone"
                    class="join-input"
                    type="tel"
                    :placeholder="placeholders.phone"
                    required
                  >
                </div>

                <template v-if="isGroomer">
                  <div class="w-100 d-inline-block mb-2">
                    <label class="join-label inter-regular-family text-black text-full-11">
                      {{ labels.mailingAddress }}<span class="join-req" aria-hidden="true">*</span>
                    </label>
                    <input
                      v-model.trim="form.mailingAddress"
                      class="join-input"
                      type="text"
                      :placeholder="placeholders.mailingAddress"
                      required
                    >
                  </div>

                  <div class="w-100 d-inline-block mb-2">
                    <label class="join-label inter-regular-family text-black text-full-11">
                      {{ labels.yearsExperience }}<span class="join-req" aria-hidden="true">*</span>
                    </label>
                    <select
                      v-model="form.yearsExperience"
                      class="join-input join-input--select"
                      required
                    >
                      <option value="" disabled>{{ placeholders.yearsExperience }}</option>
                      <option v-for="o in yearsExperienceOptions" :key="o.value" :value="o.value">
                        {{ o.label }}
                      </option>
                    </select>
                  </div>
                </template>

                <div class="w-100 d-inline-block join-legal inter-regular-family text-muted text-full-10">
                  {{ legalText }}
                </div>

                <button type="submit" class="join-cta join-cta--centered" :disabled="!canProceed || checkingStep1Email">
                  <span class="inter-bold-family text-uppercase text-full-11">{{ checkingStep1Email ? 'Checking…' : nextButtonText }}</span>
                </button>
              </form>

              <form v-else-if="step === 2 && isSalon" class="w-100 d-inline-block mt-3" @submit.prevent="goNext">
                <div class="w-100 d-inline-block mb-2">
                  <label class="join-label inter-regular-family text-black text-full-11">
                    {{ step2Labels.legalSalonName }}<span class="join-req" aria-hidden="true">*</span>
                  </label>
                  <input
                    v-model.trim="form.legalSalonName"
                    class="join-input"
                    type="text"
                    :placeholder="step2Placeholders.legalSalonName"
                    required
                  >
                </div>

                <div class="w-100 d-inline-block mb-2">
                  <label class="join-label inter-regular-family text-black text-full-11">
                    {{ step2Labels.physicalAddress }}<span class="join-req" aria-hidden="true">*</span>
                  </label>
                  <input
                    v-model.trim="form.physicalAddress"
                    class="join-input"
                    type="text"
                    :placeholder="step2Placeholders.physicalAddress"
                    required
                  >
                </div>

                <div class="w-100 d-inline-block mb-2">
                  <label class="join-label inter-regular-family text-black text-full-11">
                    {{ step2Labels.taxId }}
                  </label>
                  <input
                    v-model.trim="form.taxId"
                    class="join-input"
                    type="text"
                    :placeholder="step2Placeholders.taxId"
                  >
                </div>

                <div class="w-100 d-inline-block mb-2">
                  <label class="join-label inter-regular-family text-black text-full-11">
                    {{ step2Labels.groomingStations }}<span class="join-req" aria-hidden="true">*</span>
                  </label>
                  <select v-model="form.groomingStations" class="join-input join-input--select" required>
                    <option value="" disabled>{{ step2Placeholders.groomingStations }}</option>
                    <option v-for="o in step2GroomingStationsOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
                  </select>
                </div>

                <div class="w-100 d-inline-block mb-2">
                  <label class="join-label inter-regular-family text-black text-full-11">
                    {{ step2Labels.bathingTubs }}<span class="join-req" aria-hidden="true">*</span>
                  </label>
                  <select v-model="form.bathingTubs" class="join-input join-input--select" required>
                    <option value="" disabled>{{ step2Placeholders.bathingTubs }}</option>
                    <option v-for="o in step2BathingTubsOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
                  </select>
                </div>

                <div class="w-100 d-inline-block mb-2">
                  <label class="join-label inter-regular-family text-black text-full-11">
                    {{ step2Labels.groomersCount }}<span class="join-req" aria-hidden="true">*</span>
                  </label>
                  <select v-model="form.groomersCount" class="join-input join-input--select" required>
                    <option value="" disabled>{{ step2Placeholders.groomersCount }}</option>
                    <option v-for="o in step2GroomersCountOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
                  </select>
                </div>

                <div class="w-100 d-inline-block mb-2">
                  <div class="inter-regular-family text-black text-full-11 mb-2">
                    {{ step2Labels.hasInsurance }}<span class="join-req" aria-hidden="true">*</span>
                  </div>
                  <div class="d-flex align-items-center gap-4">
                    <label class="d-inline-flex align-items-center gap-2 inter-regular-family text-black text-full-11">
                      <input type="radio" name="insurance" value="yes" v-model="form.hasInsurance" required>
                      Yes
                    </label>
                    <label class="d-inline-flex align-items-center gap-2 inter-regular-family text-black text-full-11">
                      <input type="radio" name="insurance" value="no" v-model="form.hasInsurance" required>
                      No
                    </label>
                  </div>
                </div>

                <div class="w-100 d-inline-block join-legal inter-regular-family text-muted text-full-10">
                  {{ step2LegalText }}
                </div>

                <button type="submit" class="join-cta join-cta--centered" :disabled="!canProceedStep2Salon">
                  <span class="inter-bold-family text-uppercase text-full-11">{{ step2ButtonText }}</span>
                </button>
              </form>

              <form v-else-if="step === 2 && isGroomer" class="w-100 d-inline-block mt-3" @submit.prevent="goNext">
                <div class="w-100 d-inline-block mb-2">
                  <div class="inter-regular-family text-black text-full-11 mb-2">
                    {{ groomerStep2Labels.primaryServiceType }}<span class="join-req" aria-hidden="true">*</span>
                  </div>
                  <div class="join-cards">
                    <button
                      type="button"
                      class="join-card-option"
                      :class="{ 'join-card-option--active': form.primaryServiceType === 'pet_groomer' }"
                      @click="form.primaryServiceType = 'pet_groomer'"
                    >
                      <div class="inter-bold-family text-black text-full-11">Pet Groomer</div>
                      <div class="text-muted inter-regular-family text-full-10">
                        {{ groomerStep2Help.petGroomer }}
                      </div>
                    </button>
                    <button
                      type="button"
                      class="join-card-option"
                      :class="{ 'join-card-option--active': form.primaryServiceType === 'pet_bather' }"
                      @click="form.primaryServiceType = 'pet_bather'"
                    >
                      <div class="inter-bold-family text-black text-full-11">Pet Bather</div>
                      <div class="text-muted inter-regular-family text-full-10">
                        {{ groomerStep2Help.petBather }}
                      </div>
                    </button>
                  </div>
                </div>

                <div class="w-100 d-inline-block mb-2">
                  <label class="join-label inter-regular-family text-black text-full-11">
                    {{ groomerStep2Labels.petsGroomed }}<span class="join-req" aria-hidden="true">*</span>
                  </label>
                  <select v-model="form.petsGroomed" class="join-input join-input--select" required>
                    <option value="" disabled>{{ groomerStep2Placeholders.petsGroomed }}</option>
                    <option v-for="o in groomerStep2PetsGroomedOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
                  </select>
                </div>

                <div class="w-100 d-inline-block mb-2">
                  <div class="inter-regular-family text-black text-full-11 mb-1">
                    {{ groomerStep2Labels.petsServiced }}<span class="join-req" aria-hidden="true">*</span>
                    <span class="text-muted inter-regular-family text-full-10">({{ groomerStep2Placeholders.petsServiced }})</span>
                  </div>
                  <div class="join-checks">
                    <label v-for="p in groomerStep2PetsServicedOptions" :key="p.value" class="join-check">
                      <input type="checkbox" :value="p.value" v-model="form.petsServiced">
                      <span class="inter-regular-family text-black text-full-11">{{ p.label }}</span>
                    </label>
                  </div>
                </div>

                <div class="w-100 d-inline-block mb-2">
                  <div class="inter-regular-family text-black text-full-11 mb-1">
                    {{ groomerStep2Labels.hasCertifications }}<span class="join-req" aria-hidden="true">*</span>
                  </div>
                  <div class="d-flex align-items-center gap-4">
                    <label class="d-inline-flex align-items-center gap-2 inter-regular-family text-black text-full-11">
                      <input type="radio" name="hasCertifications" value="yes" v-model="form.hasCertifications">
                      Yes
                    </label>
                    <label class="d-inline-flex align-items-center gap-2 inter-regular-family text-black text-full-11">
                      <input type="radio" name="hasCertifications" value="no" v-model="form.hasCertifications">
                      No
                    </label>
                  </div>
                </div>

                <div v-if="form.hasCertifications === 'yes'" class="w-100 d-inline-block mb-2">
                  <label class="join-label inter-regular-family text-black text-full-11">
                    {{ groomerStep2Labels.certifications }}<span class="join-req" aria-hidden="true">*</span>
                  </label>
                  <input
                    v-model.trim="form.certifications"
                    class="join-input"
                    type="text"
                    :placeholder="groomerStep2Placeholders.certifications"
                    required
                  >
                </div>

                <div class="w-100 d-inline-block mb-2">
                  <div class="inter-regular-family text-black text-full-11 mb-1">
                    {{ groomerStep2Labels.hasDriversLicense }}<span class="join-req" aria-hidden="true">*</span>
                  </div>
                  <div class="d-flex align-items-center gap-4">
                    <label class="d-inline-flex align-items-center gap-2 inter-regular-family text-black text-full-11">
                      <input type="radio" name="hasDriversLicense" value="yes" v-model="form.hasDriversLicense">
                      Yes
                    </label>
                    <label class="d-inline-flex align-items-center gap-2 inter-regular-family text-black text-full-11">
                      <input type="radio" name="hasDriversLicense" value="no" v-model="form.hasDriversLicense">
                      No
                    </label>
                  </div>
                </div>

                <div class="w-100 d-inline-block mb-2">
                  <div class="inter-regular-family text-black text-full-11 mb-1">
                    {{ groomerStep2Labels.preferredServiceType }}<span class="join-req" aria-hidden="true">*</span>
                    <span class="text-muted inter-regular-family text-full-10">({{ groomerStep2Placeholders.preferredServiceType }})</span>
                  </div>
                  <div class="join-checks">
                    <label v-for="p in groomerStep2PreferredServiceOptions" :key="p.value" class="join-check">
                      <input type="checkbox" :value="p.value" v-model="form.preferredServiceType">
                      <span class="inter-regular-family text-black text-full-11">{{ p.label }}</span>
                    </label>
                  </div>
                </div>

                <div class="w-100 d-inline-block mb-2">
                  <div class="inter-regular-family text-black text-full-11 mb-1">
                    {{ groomerStep2Labels.hasTools }}<span class="join-req" aria-hidden="true">*</span>
                  </div>
                  <div class="d-flex align-items-center gap-4">
                    <label class="d-inline-flex align-items-center gap-2 inter-regular-family text-black text-full-11">
                      <input type="radio" name="hasTools" value="yes" v-model="form.hasTools">
                      Yes
                    </label>
                    <label class="d-inline-flex align-items-center gap-2 inter-regular-family text-black text-full-11">
                      <input type="radio" name="hasTools" value="no" v-model="form.hasTools">
                      No
                    </label>
                  </div>
                </div>

                <div class="w-100 d-inline-block mb-2">
                  <div class="inter-regular-family text-black text-full-11 mb-1">
                    {{ groomerStep2Labels.hasTaxId }}<span class="join-req" aria-hidden="true">*</span>
                  </div>
                  <div class="d-flex align-items-center gap-4">
                    <label class="d-inline-flex align-items-center gap-2 inter-regular-family text-black text-full-11">
                      <input type="radio" name="hasTaxId" value="yes" v-model="form.hasTaxId">
                      Yes
                    </label>
                    <label class="d-inline-flex align-items-center gap-2 inter-regular-family text-black text-full-11">
                      <input type="radio" name="hasTaxId" value="no" v-model="form.hasTaxId">
                      No
                    </label>
                  </div>
                </div>

                <div class="w-100 d-inline-block join-legal inter-regular-family text-muted text-full-10">
                  {{ step2LegalText }}
                </div>

                <button type="submit" class="join-cta join-cta--centered" :disabled="!canProceedStep2Groomer">
                  <span class="inter-bold-family text-uppercase text-full-11">{{ step2ButtonText }}</span>
                </button>
              </form>

              <form v-else-if="step === 3 && isGroomer" class="w-100 d-inline-block mt-3" @submit.prevent="goNext">
                <div class="w-100 d-inline-block mb-2">
                  <div class="inter-regular-family text-black text-full-11 mb-1">
                    {{ groomerStep3Labels.workCurrently }}<span class="join-req" aria-hidden="true">*</span>
                  </div>
                  <div class="d-flex align-items-center gap-4">
                    <label class="d-inline-flex align-items-center gap-2 inter-regular-family text-black text-full-11">
                      <input type="radio" name="workCurrently" value="yes" v-model="form.workCurrently" required>
                      Yes
                    </label>
                    <label class="d-inline-flex align-items-center gap-2 inter-regular-family text-black text-full-11">
                      <input type="radio" name="workCurrently" value="no" v-model="form.workCurrently" required>
                      No
                    </label>
                  </div>
                </div>

                <div class="w-100 d-inline-block mb-2">
                  <div class="inter-regular-family text-black text-full-11 mb-1">
                    {{ groomerStep3Labels.hasReferences }}<span class="join-req" aria-hidden="true">*</span>
                  </div>
                  <div class="d-flex align-items-center gap-4">
                    <label class="d-inline-flex align-items-center gap-2 inter-regular-family text-black text-full-11">
                      <input type="radio" name="hasReferences" value="yes" v-model="form.hasReferences" required>
                      Yes
                    </label>
                    <label class="d-inline-flex align-items-center gap-2 inter-regular-family text-black text-full-11">
                      <input type="radio" name="hasReferences" value="no" v-model="form.hasReferences" required>
                      No
                    </label>
                  </div>
                </div>

                <div class="w-100 d-inline-block mb-2">
                  <label class="join-label inter-regular-family text-black text-full-11">
                    {{ groomerStep3Labels.instagram }}
                  </label>
                  <input v-model.trim="form.groomerInstagram" class="join-input" type="text" :placeholder="groomerStep3Placeholders.social">
                </div>

                <div class="w-100 d-inline-block mb-2">
                  <label class="join-label inter-regular-family text-black text-full-11">
                    {{ groomerStep3Labels.facebook }}
                  </label>
                  <input v-model.trim="form.groomerFacebook" class="join-input" type="text" :placeholder="groomerStep3Placeholders.social">
                </div>

                <div class="w-100 d-inline-block mb-2">
                  <label class="join-label inter-regular-family text-black text-full-11">
                    {{ groomerStep3Labels.tiktok1 }}
                  </label>
                  <input v-model.trim="form.groomerTiktok1" class="join-input" type="text" :placeholder="groomerStep3Placeholders.social">
                </div>

                <div class="w-100 d-inline-block mb-2">
                  <label class="join-label inter-regular-family text-black text-full-11">
                    {{ groomerStep3Labels.tiktok2 }}
                  </label>
                  <input v-model.trim="form.groomerTiktok2" class="join-input" type="text" :placeholder="groomerStep3Placeholders.social">
                </div>

                <div class="w-100 d-inline-block mb-2">
                  <label class="join-label inter-regular-family text-black text-full-11">
                    {{ groomerStep3Labels.workPhotos }}
                  </label>
                  <div class="join-work-upload" @click="$refs.groomerWorkPhotoInput?.click()">
                    <div class="join-work-upload-plus" aria-hidden="true">+</div>
                    <div class="flex-grow-1"></div>
                    <div class="inter-regular-family text-muted text-full-10 text-end w-100">
                      You can add multiple pictures
                    </div>
                  </div>
                  <input
                    ref="groomerWorkPhotoInput"
                    class="d-none"
                    type="file"
                    accept="image/*"
                    multiple
                    @change="onGroomerWorkPhotosChange"
                  >
                </div>

                <div class="d-flex flex-wrap gap-2 mt-2" v-if="groomerWorkPhotoPreviews.length">
                  <div v-for="(p, idx) in groomerWorkPhotoPreviews" :key="`gph-${idx}`" class="join-photo-thumb">
                    <img :src="p" alt="Uploaded work photo preview">
                  </div>
                </div>

                <div class="w-100 d-inline-block mb-2 mt-2">
                  <label class="join-label inter-regular-family text-black text-full-11">
                    Upload CV (optional)
                  </label>
                  <div class="join-cv-upload" @click="$refs.groomerCvInput?.click()">
                    <div class="join-cv-upload-icon" aria-hidden="true">
                      <i class="fa fa-file-text-o" aria-hidden="true"></i>
                    </div>
                    <div class="join-cv-upload-text">
                      <div class="inter-regular-family text-black text-full-11">
                        {{ groomerCvName || 'Select a file (PDF/DOC/DOCX)' }}
                      </div>
                      <div class="inter-regular-family text-muted text-full-10">
                        Max size depends on backend limits.
                      </div>
                    </div>
                  </div>
                  <input
                    ref="groomerCvInput"
                    class="d-none"
                    type="file"
                    accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    @change="onGroomerCvChange"
                  >
                </div>

                <div class="w-100 d-inline-block mb-2">
                  <label class="join-label inter-regular-family text-black text-full-11">
                    Service pin location<span class="join-req" aria-hidden="true">*</span>
                  </label>
                  <input
                    :value="joinPinSummaryDisplay"
                    readonly
                    class="join-input"
                    placeholder="Open map to set your exact service pin"
                    @click="openJoinPinModal"
                  >
                  <button
                    type="button"
                    class="join-cta join-cta--centered mt-2"
                    @click="openJoinPinModal"
                  >
                    <span class="inter-bold-family text-uppercase text-full-11">Set pin on map</span>
                  </button>
                </div>

                <div class="w-100 d-inline-block mb-2">
                  <label class="join-label inter-regular-family text-black text-full-11">
                    {{ groomerStep3Labels.learnGrooming }}<span class="join-req" aria-hidden="true">*</span>
                  </label>
                  <textarea
                    v-model.trim="form.groomerLearnGrooming"
                    class="join-textarea"
                    rows="4"
                    :placeholder="groomerStep3Placeholders.learnGrooming"
                    required
                  ></textarea>
                </div>

                <div class="w-100 d-inline-block join-legal inter-regular-family text-black text-full-10 text-center">
                  {{ groomerStep3LegalText }}
                </div>

                <button type="submit" class="join-cta join-cta--centered" :disabled="!canProceedStep3Groomer || isSubmitting">
                  <span class="inter-bold-family text-uppercase text-full-11">{{ isSubmitting ? 'SUBMITTING...' : groomerStep3ButtonText }}</span>
                </button>
              </form>

              <form v-else-if="step === 3 && isSalon" class="w-100 d-inline-block mt-3" @submit.prevent="goNext">
                <div class="inter-regular-family text-black text-full-11 mb-2">
                  {{ step3SectionHours }}<span class="join-req" aria-hidden="true">*</span>
                </div>

                <div class="join-hours">
                  <div
                    v-for="d in weekDays"
                    :key="`hours-${d.key}`"
                    class="join-hours-row"
                    :class="{ 'join-hours-row--inactive': !d.model.open }"
                  >
                    <label class="join-hours-left">
                      <input type="checkbox" v-model="d.model.open">
                      <span class="inter-regular-family text-black text-full-11">{{ d.label }}</span>
                    </label>

                    <div class="join-hours-times">
                      <div class="join-time">
                        <i class="fa fa-clock-o text-muted" aria-hidden="true"></i>
                        <input
                          class="join-time-input"
                          type="time"
                          v-model="d.model.start"
                          :disabled="!d.model.open || d.model.fullDay"
                        >
                      </div>
                      <div class="join-time">
                        <i class="fa fa-clock-o text-muted" aria-hidden="true"></i>
                        <input
                          class="join-time-input"
                          type="time"
                          v-model="d.model.end"
                          :disabled="!d.model.open || d.model.fullDay"
                        >
                      </div>
                    </div>

                    <label class="join-switch" :class="{ 'join-switch--on': d.model.open }">
                      <input type="checkbox" v-model="d.model.fullDay" :disabled="!d.model.open">
                      <span class="join-switch-ui" aria-hidden="true"></span>
                    </label>
                  </div>
                </div>

                <div class="inter-regular-family text-black text-full-11 mt-3 mb-2">
                  {{ step3SectionServices }}<span class="join-req" aria-hidden="true">*</span>
                </div>

                <div class="join-checks">
                  <label v-for="s in serviceOfferedDefs" :key="s.id" class="join-check">
                    <input type="checkbox" v-model="form.serviceOffered" :value="s.id">
                    <span class="inter-regular-family text-black text-full-11">{{ s.label }}</span>
                  </label>
                </div>

                <div class="w-100 d-inline-block mb-2">
                  <textarea
                    v-model.trim="form.serviceOtherText"
                    class="join-textarea"
                    rows="4"
                    placeholder="add others services"
                  ></textarea>
                </div>

                <div class="inter-regular-family text-black text-full-11 mb-2">
                  {{ step3SectionPhotos }}
                </div>

                <div class="join-dropzone" @click="$refs.salonPhotoInput?.click()">
                  <div class="join-dropzone-plus" aria-hidden="true">+</div>
                  <div class="inter-regular-family text-muted text-full-10 mt-2">
                    You can select multiple pictures
                  </div>
                </div>
                <input
                  ref="salonPhotoInput"
                  class="d-none"
                  type="file"
                  accept="image/*"
                  multiple
                  @change="onSalonPhotosChange"
                >

                <div class="d-flex flex-wrap gap-2 mt-2" v-if="photoPreviews.length">
                  <div v-for="(p, idx) in photoPreviews" :key="`ph-${idx}`" class="join-photo-thumb">
                    <img :src="p" alt="Uploaded photo preview">
                  </div>
                </div>

                <div class="w-100 d-inline-block mb-2 mt-3">
                  <label class="join-label inter-regular-family text-black text-full-11">
                    Service pin location<span class="join-req" aria-hidden="true">*</span>
                  </label>
                  <input
                    :value="joinPinSummaryDisplay"
                    readonly
                    class="join-input"
                    placeholder="Open map to set your salon pin"
                    @click="openJoinPinModal"
                  >
                  <button
                    type="button"
                    class="join-cta join-cta--centered mt-2"
                    @click="openJoinPinModal"
                  >
                    <span class="inter-bold-family text-uppercase text-full-11">Set pin on map</span>
                  </button>
                </div>

                <div class="w-100 d-inline-block mb-2 mt-3">
                  <label class="join-label inter-regular-family text-black text-full-11">
                    {{ step3Labels.websiteUrl }}
                  </label>
                  <input v-model.trim="form.websiteUrl" class="join-input" type="text" :placeholder="step3Placeholders.websiteUrl">
                </div>

                <div class="w-100 d-inline-block mb-2">
                  <label class="join-label inter-regular-family text-black text-full-11">
                    {{ step3Labels.facebook }}
                  </label>
                  <input v-model.trim="form.facebook" class="join-input" type="text" :placeholder="step3Placeholders.facebook">
                </div>

                <div class="w-100 d-inline-block mb-2">
                  <label class="join-label inter-regular-family text-black text-full-11">
                    {{ step3Labels.tiktok }}
                  </label>
                  <input v-model.trim="form.tiktok" class="join-input" type="text" :placeholder="step3Placeholders.tiktok">
                </div>

                <div class="w-100 d-inline-block mb-2">
                  <label class="join-label inter-regular-family text-black text-full-11">
                    {{ step3Labels.referral }}<span class="join-req" aria-hidden="true">*</span>
                  </label>
                  <select v-model="form.referral" class="join-input join-input--select" required>
                    <option value="" disabled>{{ step3Placeholders.referral }}</option>
                    <option v-for="o in step3ReferralOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
                  </select>
                </div>

                <div class="w-100 d-inline-block join-legal inter-regular-family text-black text-full-10 text-center">
                  {{ step3LegalText }}
                </div>

                <button type="submit" class="join-cta join-cta--centered" :disabled="!canProceedStep3Salon || isSubmitting">
                  <span class="inter-bold-family text-uppercase text-full-11">{{ isSubmitting ? 'SUBMITTING...' : step3ButtonText }}</span>
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>

    <footer-component />
  </div>
</template>

<script>
import headerComponent from '../header/headerComponent.vue';
import footerComponent from '../footer/footerComponent.vue';
import PartnerPinLocationModal from '@/components/dashboard/PartnerPinLocationModal.vue';
import { writePartnerApplicationSnapshot } from '@/utils/partnerProfileStorage';
import { readCountrySlugFromStorage, readCountryNameFromStorage } from '@/utils/currentCountry';

export default {
  name: 'ApplySalonJoinPage',
  components: {
    headerComponent,
    footerComponent,
    PartnerPinLocationModal,
  },
  data() {
    return {
      salonPage: null,
      activeCountries: [],
      isCountriesLoading: false,
      step: 1,
      salonPhotoFiles: [],
      photoPreviews: [],
      groomerWorkPhotoFiles: [],
      groomerWorkPhotoPreviews: [],
      groomerCvFile: null,
      isSubmitting: false,
      step1EmailError: '',
      checkingStep1Email: false,
      form: {
        joinType: '',
        countrySlug: '',
        countryName: '',
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        pin_lat: null,
        pin_lng: null,
        pin_address: '',
        mailingAddress: '',
        yearsExperience: '',
        legalSalonName: '',
        physicalAddress: '',
        taxId: '',
        groomingStations: '',
        bathingTubs: '',
        groomersCount: '',
        hasInsurance: '',
        // Salon step 3
        hours: (() => {
          const day = (open, fullDay, start, end) => ({ open, fullDay, start, end });
          return {
            monday: day(false, true, '09:00', '17:00'),
            tuesday: day(true, false, '09:00', '17:00'),
            wednesday: day(false, true, '09:00', '17:00'),
            thursday: day(false, true, '09:00', '17:00'),
            friday: day(true, false, '09:00', '17:00'),
            saturday: day(false, true, '09:00', '17:00'),
            sunday: day(true, false, '09:00', '17:00'),
          };
        })(),
        serviceOffered: [],
        serviceOtherText: '',
        websiteUrl: '',
        facebook: '',
        tiktok: '',
        referral: '',
        primaryServiceType: '',
        petsGroomed: '',
        petsServiced: [],
        hasCertifications: '',
        certifications: '',
        hasDriversLicense: '',
        preferredServiceType: [],
        hasTools: '',
        hasTaxId: '',
        // Groomer step 3
        workCurrently: '',
        hasReferences: '',
        groomerInstagram: '',
        groomerFacebook: '',
        groomerTiktok1: '',
        groomerTiktok2: '',
        groomerLearnGrooming: '',
      },
    };
  },
  watch: {
    joinOptions: {
      immediate: true,
      handler(list) {
        if (this.form.joinType) return;
        const first = Array.isArray(list) && list.length ? list[0]?.value : '';
        if (first) this.form.joinType = first;
      },
    },
    '$route.query.joinType': {
      immediate: true,
      handler(v) {
        this.applyJoinTypeFromRoute(v);
      },
    },
    'form.countrySlug': {
      immediate: false,
      handler(slug) {
        const s = String(slug || '').toLowerCase().trim();
        const match = this.activeCountries.find((c) => c.slug === s);
        if (match) {
          this.form.countryName = match.name;
        }
      },
    },
    'form.email'() {
      this.step1EmailError = '';
    },
  },
  computed: {
    groomerCvName() {
      const f = this.groomerCvFile;
      return f && f.name ? String(f.name) : '';
    },
    isGroomer() {
      return String(this.form.joinType || '').toLowerCase() === 'groomer';
    },
    isSalon() {
      return String(this.form.joinType || '').toLowerCase() === 'salon';
    },
    stepHeading() {
      if (this.step === 2) {
        const h = this.salonPage?.join_step2_heading;
        return h && String(h).trim() ? h : 'Join LushGroom';
      }
      if (this.step === 3) {
        if (this.isGroomer) {
          const h3g = this.salonPage?.join_groomer_step3_heading;
          return h3g && String(h3g).trim() ? h3g : 'Join LushGroom';
        }
        const h3 = this.salonPage?.join_step3_heading;
        return h3 && String(h3).trim() ? h3 : 'Join LushGroom';
      }
      const h1 = this.salonPage?.join_step1_heading;
      return h1 && String(h1).trim() ? h1 : 'Join LushGroom';
    },
    stepSubheading() {
      if (this.step === 2) {
        const t = this.salonPage?.join_step2_subheading;
        if (t && String(t).trim()) return t;
        return this.isGroomer ? 'Personal information' : 'Salon Specifications';
      }
      if (this.step === 3) {
        if (this.isGroomer) {
          const t3g = this.salonPage?.join_groomer_step3_subheading;
          return t3g && String(t3g).trim() ? t3g : 'Post your professional Experience!';
        }
        const t3 = this.salonPage?.join_step3_subheading;
        return t3 && String(t3).trim() ? t3 : 'Salon Profile & Operations';
      }
      const t1 = this.salonPage?.join_step1_subheading;
      if (t1 && String(t1).trim()) return t1;
      return "Let's get you started";
    },
    weekDays() {
      const defs = [
        { key: 'monday', label: 'Monday' },
        { key: 'tuesday', label: 'Tuesday' },
        { key: 'wednesday', label: 'Wednesday' },
        { key: 'thursday', label: 'Thursday' },
        { key: 'friday', label: 'Friday' },
        { key: 'saturday', label: 'Saturday' },
        { key: 'sunday', label: 'Sunday' },
      ];
      return defs.map((d) => ({ ...d, model: this.form.hours?.[d.key] }));
    },
    serviceOfferedDefs() {
      return [
        { id: 'full_groom', label: 'Full Groom' },
        { id: 'bath_brush', label: 'Bath & Brush' },
        { id: 'nail_trim', label: 'Nail Trim only' },
        { id: 'others', label: 'Others' },
      ];
    },
    step3SectionHours() {
      const t = this.salonPage?.join_step3_section_hours;
      return t && String(t).trim() ? t : 'Operating Hours';
    },
    step3SectionServices() {
      const t = this.salonPage?.join_step3_section_services;
      return t && String(t).trim() ? t : 'Services Offered';
    },
    step3SectionPhotos() {
      const t = this.salonPage?.join_step3_section_photos;
      return t && String(t).trim()
        ? t
        : 'Upload photos of your Shop Front and Grooming Area (optional).';
    },
    step3Labels() {
      const l = this.salonPage?.join_step3_labels || {};
      return {
        websiteUrl: l.websiteUrl || 'Website URL',
        facebook: l.facebook || 'Facebook Account (optional)',
        tiktok: l.tiktok || 'TikTok Account (optional)',
        referral: l.referral || 'Referral',
      };
    },
    step3Placeholders() {
      const p = this.salonPage?.join_step3_placeholders || {};
      return {
        websiteUrl: p.websiteUrl || 'www.lushgroom.com',
        facebook: p.facebook || '@name',
        tiktok: p.tiktok || '@name',
        referral: p.referral || 'Select',
      };
    },
    step3ReferralOptions() {
      const api = this.salonPage?.join_step3_referral_options;
      const normalized = Array.isArray(api)
        ? api.map((o, idx) => ({ value: String(o?.value ?? idx + 1), label: String(o?.label ?? '') })).filter((o) => o.label.trim())
        : [];
      const fallback = [
        { value: 'google', label: 'Google' },
        { value: 'facebook', label: 'Facebook' },
        { value: 'instagram', label: 'Instagram' },
        { value: 'friend', label: 'Friend / referral' },
        { value: 'other', label: 'Other' },
      ];
      return normalized.length ? normalized : fallback;
    },
    step3LegalText() {
      const t = this.salonPage?.join_step3_legal_text;
      return t && String(t).trim()
        ? t
        : 'By submitting your application you agree to our Terms and Privacy Policy.';
    },
    step3ButtonText() {
      const t = this.salonPage?.join_step3_next_text;
      return t && String(t).trim() ? t : 'CONTINUE';
    },
    joinPinSummaryDisplay() {
      const lat = Number(this.form.pin_lat);
      const lng = Number(this.form.pin_lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return '';
      const addr = String(this.form.pin_address || '').trim();
      return addr ? `${addr} (${lat}, ${lng})` : `${lat}, ${lng}`;
    },
    joinPinComplete() {
      const lat = Number(this.form.pin_lat);
      const lng = Number(this.form.pin_lng);
      return Number.isFinite(lat) && Number.isFinite(lng) && String(this.form.pin_address || '').trim().length > 0;
    },
    canProceedStep3Salon() {
      if (!this.joinPinComplete) return false;
      if (!this.form.referral) return false;
      if (!Array.isArray(this.form.serviceOffered) || !this.form.serviceOffered.length) return false;
      if (this.form.serviceOffered.includes('others') && !String(this.form.serviceOtherText || '').trim()) return false;

      const days = this.form.hours || {};
      const anyOpen = Object.values(days).some((d) => d && d.open);
      if (!anyOpen) return false;

      const dayOk = (d) => {
        if (!d || !d.open) return true;
        if (d.fullDay) return true;
        return Boolean(d.start && d.end);
      };
      return Object.values(days).every(dayOk);
    },
    joinQuestion() {
      const q = this.salonPage?.join_step1_question;
      return q && String(q).trim() ? q : 'How would you like to join LushGroom?';
    },
    joinOptions() {
      const apiOptions = this.salonPage?.join_step1_options;
      const normalized = Array.isArray(apiOptions)
        ? apiOptions
          .map((o, idx) => ({
            value: o?.value != null ? String(o.value) : `opt-${idx + 1}`,
            label: o?.label != null ? String(o.label) : '',
            help: o?.help != null ? String(o.help) : '',
          }))
          .filter((o) => o.label.trim())
        : [];

      const fallback = [
        { value: 'salon', label: 'SALON', help: '' },
        { value: 'groomer', label: 'GROOMER', help: '' },
      ];

      // Requirement: ONLY 2 options (even if API provides more).
      return (normalized.length ? normalized : fallback).slice(0, 2);
    },
    labels() {
      const base = this.salonPage?.join_step1_labels || {};
      const g = this.salonPage?.join_step1_groomer_labels || {};
      return {
        firstName: (this.isGroomer ? g.firstName : base.firstName) || (this.isGroomer ? 'First name' : 'Business owner first name'),
        lastName: (this.isGroomer ? g.lastName : base.lastName) || (this.isGroomer ? 'Last name' : 'Business owner last name'),
        phone: (this.isGroomer ? g.phone : base.phone) || (this.isGroomer ? 'Mobile phone number' : 'Business phone number'),
        email: (this.isGroomer ? g.email : base.email) || (this.isGroomer ? 'Email' : 'Email address (preferred)'),
        mailingAddress: g.mailingAddress || 'Mailing address',
        yearsExperience: g.yearsExperience || 'Years of grooming experience',
      };
    },
    placeholders() {
      const base = this.salonPage?.join_step1_placeholders || {};
      const g = this.salonPage?.join_step1_groomer_placeholders || {};
      return {
        firstName: (this.isGroomer ? g.firstName : base.firstName) || 'First Name',
        lastName: (this.isGroomer ? g.lastName : base.lastName) || 'Last Name',
        email: (this.isGroomer ? g.email : base.email) || 'email@address.com',
        phone: (this.isGroomer ? g.phone : base.phone) || 'XXX XXX XXX',
        mailingAddress: g.mailingAddress || '35, High St, Islington, NY, UK',
        yearsExperience: g.yearsExperience || 'Select',
      };
    },
    yearsExperienceOptions() {
      const api = this.salonPage?.join_step1_groomer_experience_options;
      const normalized = Array.isArray(api)
        ? api
          .map((o, idx) => ({
            value: o?.value != null ? String(o.value) : String(idx + 1),
            label: o?.label != null ? String(o.label) : '',
          }))
          .filter((o) => o.label.trim())
        : [];
      const fallback = [
        { value: '0-1', label: '0–1 years' },
        { value: '2-4', label: '2–4 years' },
        { value: '5-7', label: '5–7 years' },
        { value: '8-10', label: '8–10 years' },
        { value: '10+', label: '10+ years' },
      ];
      return normalized.length ? normalized : fallback;
    },
    nextButtonText() {
      const t = this.salonPage?.join_step1_next_text;
      return t && String(t).trim() ? t : 'CONTINUE';
    },
    legalText() {
      const t = this.salonPage?.join_step1_legal_text;
      return t && String(t).trim()
        ? t
        : 'By submitting, you agree to be contacted by LushGroom. Message and data rates may apply. You can opt out anytime.';
    },
    canProceed() {
      const baseOk = Boolean(
        this.form.joinType
        && this.form.countrySlug
        && this.form.email
        && this.form.phone
        && this.form.firstName
        && this.form.lastName
      );
      if (!baseOk) return false;
      if (!this.isGroomer) return true;
      return Boolean(this.form.mailingAddress && this.form.yearsExperience);
    },
    step2Labels() {
      const l = this.salonPage?.join_step2_labels || {};
      return {
        legalSalonName: l.legalSalonName || 'Legal Salon Name',
        physicalAddress: l.physicalAddress || 'Physical Salon Address',
        taxId: l.taxId || 'EIN / Tax ID Number',
        groomingStations: l.groomingStations || 'Number of Grooming Stations',
        bathingTubs: l.bathingTubs || 'Number of Bathing Tubs',
        groomersCount: l.groomersCount || 'How many groomers currently work at your location?',
        hasInsurance: l.hasInsurance || 'Do you have active Business Liability insurance?',
      };
    },
    step2Placeholders() {
      const p = this.salonPage?.join_step2_placeholders || {};
      return {
        legalSalonName: p.legalSalonName || 'Salon Name',
        physicalAddress: p.physicalAddress || '1564, Jadkh, NY, USA',
        taxId: p.taxId || 'EIN / Tax ID Number',
        groomingStations: p.groomingStations || 'Number of Grooming Stations',
        bathingTubs: p.bathingTubs || 'Number of Bathing Tubs',
        groomersCount: p.groomersCount || 'Select',
      };
    },
    step2GroomingStationsOptions() {
      const api = this.salonPage?.join_step2_grooming_station_options;
      const normalized = Array.isArray(api)
        ? api.map((o, idx) => ({ value: String(o?.value ?? idx + 1), label: String(o?.label ?? '') })).filter((o) => o.label.trim())
        : [];
      const fallback = Array.from({ length: 10 }).map((_, i) => ({ value: String(i + 1), label: String(i + 1) }));
      return normalized.length ? normalized : fallback;
    },
    step2BathingTubsOptions() {
      const api = this.salonPage?.join_step2_bathing_tub_options;
      const normalized = Array.isArray(api)
        ? api.map((o, idx) => ({ value: String(o?.value ?? idx + 1), label: String(o?.label ?? '') })).filter((o) => o.label.trim())
        : [];
      const fallback = Array.from({ length: 10 }).map((_, i) => ({ value: String(i + 1), label: String(i + 1) }));
      return normalized.length ? normalized : fallback;
    },
    step2GroomersCountOptions() {
      const api = this.salonPage?.join_step2_groomers_count_options;
      const normalized = Array.isArray(api)
        ? api.map((o, idx) => ({ value: String(o?.value ?? idx + 1), label: String(o?.label ?? '') })).filter((o) => o.label.trim())
        : [];
      const fallback = Array.from({ length: 21 }).map((_, i) => ({ value: String(i), label: i === 20 ? '20+' : String(i) }));
      return normalized.length ? normalized : fallback;
    },
    step2LegalText() {
      const t = this.salonPage?.join_step2_legal_text;
      return t && String(t).trim()
        ? t
        : 'Your privacy and security are our top priority. Your information is used solely for payment processing and tax purposes.';
    },
    step2ButtonText() {
      const t = this.salonPage?.join_step2_next_text;
      return t && String(t).trim() ? t : 'CONTINUE';
    },
    canProceedStep2Salon() {
      const filled = (v) => String(v ?? '').trim().length > 0;
      const hasInsuranceOk = this.form.hasInsurance === 'yes' || this.form.hasInsurance === 'no';
      return (
        filled(this.form.legalSalonName)
        && filled(this.form.physicalAddress)
        && filled(this.form.groomingStations)
        && filled(this.form.bathingTubs)
        && filled(this.form.groomersCount)
        && hasInsuranceOk
      );
    },
    groomerStep2Labels() {
      const l = this.salonPage?.join_groomer_step2_labels || {};
      return {
        primaryServiceType: l.primaryServiceType || 'Select your primary service type',
        petsGroomed: l.petsGroomed || 'How many pets groomed within last 2 years?',
        petsServiced: l.petsServiced || 'What pets do you service?',
        hasCertifications: l.hasCertifications || 'Do you hold any grooming certifications?',
        certifications: l.certifications || 'Which certifications?',
        hasDriversLicense: l.hasDriversLicense || "Do you have a valid driver's license?",
        preferredServiceType: l.preferredServiceType || 'What Service type do you prefer?',
        hasTools: l.hasTools || 'Do you have grooming tools?',
        hasTaxId: l.hasTaxId || 'Do you have a valid SSN or Tax ID?',
      };
    },
    groomerStep2Placeholders() {
      const p = this.salonPage?.join_groomer_step2_placeholders || {};
      return {
        petsGroomed: p.petsGroomed || 'Select',
        petsServiced: p.petsServiced || 'Select one or more that applies',
        certifications: p.certifications || 'Enter Name and School',
        preferredServiceType: p.preferredServiceType || 'Select one or more that applies',
      };
    },
    groomerStep2Help() {
      const h = this.salonPage?.join_groomer_step2_help || {};
      return {
        petGroomer: h.petGroomer || 'Full grooming services including cuts, styling, and coat maintenance',
        petBather: h.petBather || 'Bathing, blow-dry, nail trims, and coat maintenance',
      };
    },
    groomerStep2PetsGroomedOptions() {
      const api = this.salonPage?.join_groomer_step2_pets_groomed_options;
      const normalized = Array.isArray(api)
        ? api.map((o, idx) => ({ value: String(o?.value ?? idx + 1), label: String(o?.label ?? '') })).filter((o) => o.label.trim())
        : [];
      const fallback = [
        { value: '0-25', label: '0–25' },
        { value: '26-50', label: '26–50' },
        { value: '51-100', label: '51–100' },
        { value: '101-200', label: '101–200' },
        { value: '200+', label: '200+' },
      ];
      return normalized.length ? normalized : fallback;
    },
    groomerStep2PetsServicedOptions() {
      const api = this.salonPage?.join_groomer_step2_pets_serviced_options;
      const normalized = Array.isArray(api)
        ? api.map((o) => ({ value: String(o?.value ?? ''), label: String(o?.label ?? '') })).filter((o) => o.value && o.label.trim())
        : [];
      const fallback = [
        { value: 'dogs', label: 'Dogs' },
        { value: 'cats', label: 'Cats' },
        { value: 'rabbits', label: 'Rabbits' },
        { value: 'parrots', label: 'Parrots' },
      ];
      return normalized.length ? normalized : fallback;
    },
    groomerStep2PreferredServiceOptions() {
      const api = this.salonPage?.join_groomer_step2_preferred_service_options;
      const normalized = Array.isArray(api)
        ? api.map((o) => ({ value: String(o?.value ?? ''), label: String(o?.label ?? '') })).filter((o) => o.value && o.label.trim())
        : [];
      const fallback = [
        { value: 'mobile', label: 'Mobile*' },
        { value: 'in_home', label: 'In-home' },
      ];
      return normalized.length ? normalized : fallback;
    },
    canProceedStep2Groomer() {
      const petsOk = Array.isArray(this.form.petsServiced) && this.form.petsServiced.length > 0;
      const prefOk = Array.isArray(this.form.preferredServiceType) && this.form.preferredServiceType.length > 0;
      const certOk = this.form.hasCertifications === 'no' || (this.form.hasCertifications === 'yes' && Boolean(this.form.certifications));
      return Boolean(
        this.form.primaryServiceType
        && this.form.petsGroomed
        && petsOk
        && this.form.hasCertifications
        && certOk
        && this.form.hasDriversLicense
        && prefOk
        && this.form.hasTools
        && this.form.hasTaxId
      );
    },
    groomerStep3Kicker() {
      const t = this.salonPage?.join_groomer_step3_kicker;
      return t && String(t).trim() ? t : "LET'S GET YOU STARTED";
    },
    groomerStep3Tagline() {
      const t = this.salonPage?.join_groomer_step3_tagline;
      return t && String(t).trim() ? t : 'Post your professional Experience!';
    },
    groomerStep3Labels() {
      const l = this.salonPage?.join_groomer_step3_labels || {};
      return {
        workCurrently: l.workCurrently || 'Do you work currently in salon, Mobile or In-Home?',
        hasReferences: l.hasReferences || 'Do you have any references?',
        instagram: l.instagram || 'Instagram Account (optional)',
        facebook: l.facebook || 'Facebook Account (optional)',
        tiktok1: l.tiktok1 || 'Tiktok Account (optional)',
        tiktok2: l.tiktok2 || 'Tiktok Account (optional)',
        workPhotos: l.workPhotos || 'Upload pictures of your work (optional)',
        learnGrooming: l.learnGrooming || 'Where did you learn grooming?',
      };
    },
    groomerStep3Placeholders() {
      const p = this.salonPage?.join_groomer_step3_placeholders || {};
      return {
        social: p.social || '@name',
        learnGrooming: p.learnGrooming || 'I love grooming because...',
      };
    },
    groomerStep3LegalText() {
      const t = this.salonPage?.join_groomer_step3_legal_text;
      return t && String(t).trim()
        ? t
        : 'By submitting your application you agree with our Terms and Privacy Policy';
    },
    groomerStep3ButtonText() {
      const t = this.salonPage?.join_groomer_step3_next_text;
      return t && String(t).trim() ? t : 'CONTINUE';
    },
    canProceedStep3Groomer() {
      return Boolean(
        this.joinPinComplete
        && this.form.workCurrently
        && this.form.hasReferences
        && String(this.form.groomerLearnGrooming || '').trim()
      );
    },
  },
  mounted() {
    this.fetchSalonJoinConfig();
    this.fetchActiveCountries();
  },
  methods: {
    openJoinPinModal() {
      this.$refs.joinPinModal?.open({
        lat: this.form.pin_lat,
        lng: this.form.pin_lng,
        address: this.form.pin_address,
      });
    },
    onJoinPinConfirmed({ address, lat, lng }) {
      this.form.pin_address = address;
      this.form.pin_lat = lat;
      this.form.pin_lng = lng;
      this.$toast?.success('Pin location saved.');
    },
    async fetchActiveCountries() {
      if (this.isCountriesLoading) return;
      this.isCountriesLoading = true;
      try {
        const res = await this.$axios.get('/get-countries');
        const list = Array.isArray(res?.data?.countries) ? res.data.countries : [];
        // Backend already filters by countries.status = 1, so treat these as active.
        this.activeCountries = list
          .map((c) => ({
            slug: String(c?.slug || '').toLowerCase().trim(),
            name: String(c?.name || '').trim(),
          }))
          .filter((c) => c.slug && c.name);

        const preferred = readCountrySlugFromStorage();
        const fallback = String(res?.data?.default_country_slug || '').toLowerCase().trim();
        const first = this.activeCountries[0]?.slug || '';
        const chosen =
          this.activeCountries.some((c) => c.slug === preferred) ? preferred
            : (this.activeCountries.some((c) => c.slug === fallback) ? fallback : first);

        if (!this.form.countrySlug && chosen) {
          this.form.countrySlug = chosen;
        }
        if (!this.form.countryName && this.form.countrySlug) {
          const match = this.activeCountries.find((c) => c.slug === this.form.countrySlug);
          this.form.countryName = match?.name || readCountryNameFromStorage() || '';
        }
      } catch (e) {
        this.activeCountries = [];
      } finally {
        this.isCountriesLoading = false;
      }
    },
    buildPartnerApplicationFormData() {
      const payload = JSON.parse(JSON.stringify(this.form));
      const data = new FormData();
      data.append('payload', JSON.stringify(payload));

      if (this.isSalon && Array.isArray(this.salonPhotoFiles)) {
        this.salonPhotoFiles.forEach((file) => {
          if (file) data.append('salon_photos[]', file);
        });
      }

      if (this.isGroomer) {
        if (Array.isArray(this.groomerWorkPhotoFiles)) {
          this.groomerWorkPhotoFiles.forEach((file) => {
            if (file) data.append('groomer_work_photos[]', file);
          });
        }
        if (this.groomerCvFile) {
          data.append('groomer_cv', this.groomerCvFile);
        }
      }

      return data;
    },
    async submitPartnerApplication() {
      if (this.isSubmitting) return;
      this.isSubmitting = true;

      try {
        const formData = this.buildPartnerApplicationFormData();
        const res = await this.$axios.post('/partner/submit-application', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        if (res?.data?.success) {
          writePartnerApplicationSnapshot({ ...this.form, joinType: String(this.form.joinType || '').toLowerCase() });
          this.$toast?.success('Application submitted successfully.');
          this.$router.push({ name: 'applyJoinThankYou' });
          return;
        }

        this.$toast?.error(res?.data?.message || 'Unable to submit your application.');
      } catch (e) {
        const serverMessage = e?.response?.data?.message;
        const validationErrors = e?.response?.data?.errors;
        if (validationErrors && typeof validationErrors === 'object') {
          const firstKey = Object.keys(validationErrors)[0];
          const firstErr = Array.isArray(validationErrors[firstKey]) ? validationErrors[firstKey][0] : '';
          this.$toast?.error(firstErr || serverMessage || 'Please review your form and try again.');
        } else {
          this.$toast?.error(serverMessage || 'Failed to submit application. Please try again.');
        }
      } finally {
        this.isSubmitting = false;
      }
    },
    applyJoinTypeFromRoute(v) {
      const t = String(v || '').toLowerCase();
      if (t !== 'salon' && t !== 'groomer') return;
      this.form.joinType = t;
      this.step = 1;
    },
    joinOptionIconSrc(value) {
      const v = String(value || '').toLowerCase();
      if (v === 'salon') return require('@/assets/images/salon-icon.png');
      if (v === 'groomer') return require('@/assets/images/groomer-icon.png');
      return require('@/assets/images/salon-icon.png');
    },
    stepCircleClass(n) {
      if (this.step === n) return 'join-step--active';
      if (this.step > n) return 'join-step--done';
      return '';
    },
    async fetchSalonJoinConfig() {
      try {
        const res = await this.$axios.post('/get-salon-page', {
          website_name: this.activeWebsiteName,
        });
        this.salonPage = res?.data?.salon_page ?? null;
      } catch (e) {
        // keep fallbacks
      }
    },
    onJoinTypeClick(value) {
      const v = String(value || '').toLowerCase();
      this.form.joinType = v;
      this.step = 1;
    },
    onSalonPhotosChange(e) {
      const files = Array.from(e?.target?.files || []);
      this.salonPhotoFiles = files;

      this.photoPreviews.forEach((u) => {
        try { URL.revokeObjectURL(u); } catch (err) { /* ignore */ }
      });
      this.photoPreviews = files.map((f) => URL.createObjectURL(f));

      if (e?.target) e.target.value = '';
    },
    onGroomerWorkPhotosChange(e) {
      const files = Array.from(e?.target?.files || []);
      this.groomerWorkPhotoFiles = files;

      this.groomerWorkPhotoPreviews.forEach((u) => {
        try { URL.revokeObjectURL(u); } catch (err) { /* ignore */ }
      });
      this.groomerWorkPhotoPreviews = files.map((f) => URL.createObjectURL(f));

      if (e?.target) e.target.value = '';
    },
    onGroomerCvChange(e) {
      const file = (e?.target?.files && e.target.files[0]) ? e.target.files[0] : null;
      this.groomerCvFile = file;
      if (e?.target) e.target.value = '';
    },
    async checkJoinEmailAvailable() {
      const email = String(this.form.email || '').trim();
      if (!email) {
        this.step1EmailError = 'Please enter your email.';
        return false;
      }
      this.checkingStep1Email = true;
      this.step1EmailError = '';
      try {
        const res = await this.$axios.post('/partner/check-join-email', { email });
        if (res?.data?.available === false) {
          const msg = res?.data?.message || 'An account with this email already exists.';
          this.step1EmailError = msg;
          this.$toast?.error?.(msg);
          return false;
        }
        return true;
      } catch (e) {
        const errs = e?.response?.data?.errors;
        const first =
          errs && typeof errs === 'object'
            ? (Array.isArray(errs.email) ? errs.email[0] : null)
            : null;
        const msg = first || e?.response?.data?.message || 'Could not verify email. Please try again.';
        this.step1EmailError = msg;
        this.$toast?.error?.(msg);
        return false;
      } finally {
        this.checkingStep1Email = false;
      }
    },
    async goNext() {
      if (this.step === 1) {
        if (!this.canProceed) return;
        const emailOk = await this.checkJoinEmailAvailable();
        if (!emailOk) return;
        if (this.isSalon) {
          this.step = 2;
          return;
        }
        if (this.isGroomer) {
          this.step = 2;
          return;
        }
        return;
      }
      if (this.step === 2 && this.isSalon) {
        if (!this.canProceedStep2Salon) return;
        this.step = 3;
        return;
      }
      if (this.step === 2 && this.isGroomer) {
        if (!this.canProceedStep2Groomer) return;
        this.step = 3;
        return;
      }
      if (this.step === 3 && this.isGroomer) {
        if (!this.canProceedStep3Groomer) return;
        return this.submitPartnerApplication();
      }
      if (this.step === 3 && this.isSalon) {
        if (!this.canProceedStep3Salon) return;
        return this.submitPartnerApplication();
      }
    },
  },
};
</script>

<style scoped>
.join-wrap {
  background: #f7e9e2;
  position: relative;
  overflow: hidden;
  padding: 44px 0 56px;
}

.join-bg {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 320px;
  pointer-events: none;
  opacity: 1;
}
.join-bg--left {
  left: 12px;
  background: url("~@/assets/images/join-page-dog.png") left 78% / 70% auto no-repeat;
}
.join-bg--right {
  right: 12px;
  background: url("~@/assets/images/join-page-cat.png") right 22% / 70% auto no-repeat;
}

.join-card {
  padding: 18px 18px 16px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  position: relative;
  z-index: 1;
  overflow: hidden;
}

.join-page-title {
  font-size: 12px;
  letter-spacing: 0.08em;
}
.join-subtitle {
  margin-top: 4px;
  font-size: 11px;
  letter-spacing: 0.08em;
  opacity: 0.85;
}

.join-steps {
  margin: 10px auto 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}
.join-step {
  width: 22px;
  height: 22px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 11px;
  background: #EBEDEF;
  border: 1px solid #EBEDEF;
  color: rgba(0, 0, 0, 0.6);
}
.join-step--active {
  background: #f8721f;
  border-color: #f8721f;
  color: #fff;
}
.join-step--done {
  background: #111;
  border-color: #111;
  color: #fff;
}
.join-step--line {
  width: 38px;
  height: 1px;
  background: rgba(0, 0, 0, 0.2);
  border: 0;
  border-radius: 0;
  color: transparent;
}

.join-cards {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}
.join-card-option {
  text-align: left;
  width: 100%;
  border: 1px solid rgba(0, 0, 0, 0.18);
  background: #eeeeee;
  border-radius: 10px;
  padding: 10px 10px;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.join-card-option--active {
  background: rgba(248, 114, 31, 0.12);
  border-color: rgba(248, 114, 31, 0.65);
}

.join-checks {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  padding: 6px 0 2px;
}
.join-check {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.join-options {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}
.join-pill {
  border: 1px solid #EDEDED;
  background: #EDEDED;
  border-radius: 8px;
  padding: 10px 10px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-start;
  transition: background 0.15s ease, border-color 0.15s ease;
  min-height: 42px;
}
.join-pill-icon {
  width: 25px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #111;
  flex: 0 0 25px;
}
.join-pill-icon-img {
  width: 25px;
  height: 25px;
  object-fit: contain;
  display: block;
}
.join-pill-label {
  letter-spacing: 0.06em;
  flex: 1 1 auto;
  text-align: left;
}
.join-pill--active {
  background: #f8721f;
  border-color: #f8721f;
  color: #fff;
}
.join-pill--active .join-pill-icon {
  color: #fff;
}
.join-pill--active .join-pill-icon-img {
  filter: brightness(0) invert(1);
}

.join-label {
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  line-height: 1.25;
  letter-spacing: 0.02em;
  text-transform: none;
  opacity: 0.95;
}
.join-req {
  color: #f8721f;
  margin-left: 4px;
  font-weight: 900;
}
.join-input {
  width: 100%;
  border: 0;
  border-radius: 6px;
  height: 40px;
  padding: 0 12px;
  background: #f1f2f3;
  outline: none;
  font-size: 13px;
}
.join-input--select {
  appearance: none;
}
.join-input:focus {
  box-shadow: 0 0 0 3px rgba(248, 114, 31, 0.14);
}
.join-input--invalid {
  box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.35);
}
.join-field-error {
  margin-top: 6px;
  color: #dc3545;
  line-height: 1.35;
}

.join-legal {
  margin-top: 10px;
  line-height: 1.35;
}

.join-cta {
  width: 100%;
  margin-top: 12px;
  border: 0;
  height: 40px;
  border-radius: 4px;
  background: #f8721f;
  color: #fff;
  transition: opacity 0.15s ease;
}
.join-cta--centered {
  width: min(240px, 100%);
  display: block;
  margin-left: auto;
  margin-right: auto;
}
.join-cta:disabled {
  opacity: 0.55;
}

.join-cards {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}
.join-card-option {
  text-align: left;
  width: 100%;
  border: 1px solid rgba(0, 0, 0, 0.18);
  background: #eeeeee;
  border-radius: 10px;
  padding: 10px 10px;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.join-card-option--active {
  background: rgba(248, 114, 31, 0.12);
  border-color: rgba(248, 114, 31, 0.65);
}

.join-checks {
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  padding: 6px 0 2px;
}
.join-check {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.join-hours {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.join-hours-row {
  display: grid;
  grid-template-columns: 96px minmax(0, 1fr) 44px;
  align-items: center;
  gap: 6px;
  padding: 6px 0;
}
.join-hours-left {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  cursor: pointer;
  min-width: 0;
}
.join-hours-row--inactive .join-time-input:disabled,
.join-hours-row--inactive .join-time-input[readonly] {
  color: rgba(0, 0, 0, 0.28);
  background: #e9e9e9;
}
.join-hours-times {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 8px;
  min-width: 0;
}
.join-time {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #f1f2f3;
  border-radius: 6px;
  padding: 0 8px;
  height: 36px;
  min-width: 0;
}
.join-time-input {
  border: 0;
  background: transparent;
  width: 100%;
  min-width: 0;
  font-size: 12px;
  outline: none;
}
.join-switch {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  margin: 0;
  cursor: pointer;
}
.join-switch input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}
.join-switch-ui {
  width: 40px;
  height: 20px;
  border-radius: 999px;
  background: #cfcfcf;
  position: relative;
  transition: background 0.15s ease;
  display: inline-block;
}
.join-switch-ui::after {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #fff;
  transition: transform 0.15s ease;
}
.join-switch--on .join-switch-ui {
  background: #f8721f;
}
.join-switch--on .join-switch-ui::after {
  transform: translateX(20px);
}

.join-textarea {
  width: 100%;
  border: 0;
  border-radius: 8px;
  padding: 10px 12px;
  background: #f1f2f3;
  outline: none;
  font-size: 12px;
  min-height: 90px;
}

.join-dropzone {
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: #f1f2f3;
  border-radius: 10px;
  padding: 16px 12px;
  text-align: center;
  cursor: pointer;
}
.join-dropzone-plus {
  width: 64px;
  height: 64px;
  border: 2px dashed rgba(248, 114, 31, 0.65);
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #f8721f;
  font-size: 34px;
  font-weight: 800;
  line-height: 1;
}
.join-photo-thumb {
  width: 64px;
  height: 64px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.08);
}
.join-photo-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.groomer-s3-tagline {
  margin-top: 6px;
  opacity: 0.8;
  letter-spacing: 0.04em;
}

.join-work-upload {
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: #f1f2f3;
  border-radius: 10px;
  padding: 12px 12px;
  min-height: 120px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  cursor: pointer;
  gap: 10px;
}
.join-work-upload-plus {
  width: 64px;
  height: 64px;
  border: 2px dashed rgba(248, 114, 31, 0.65);
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #f8721f;
  font-size: 34px;
  font-weight: 800;
  line-height: 1;
}

.join-cv-upload {
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: #f1f2f3;
  border-radius: 10px;
  padding: 12px 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
}
.join-cv-upload-icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: rgba(248, 114, 31, 0.12);
  color: #f8721f;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 42px;
}
.join-cv-upload-text {
  min-width: 0;
}

@media (max-width: 991px) {
  .join-bg {
    width: 240px;
    opacity: 0.55;
  }
  .join-bg--left {
    background-position: left 82%;
  }
  .join-bg--right {
    background-position: right 18%;
  }
}

@media (max-width: 575px) {
  .join-bg {
    display: none;
  }
  .join-wrap {
    padding: 26px 0 34px;
  }
  /* Headings: make readable on mobile */
  .join-page-title {
    font-size: clamp(14px, 4.2vw, 18px);
    letter-spacing: 0.06em;
  }
  .join-subtitle {
    font-size: clamp(12px, 3.6vw, 15px);
    letter-spacing: 0.05em;
    line-height: 1.25;
  }
  .groomer-s3-tagline {
    font-size: clamp(12px, 3.6vw, 14px);
    line-height: 1.25;
  }

  /* In-card small headings/section titles */
  .join-card :deep(.text-full-10) {
    font-size: 12px !important;
  }
  .join-card :deep(.text-full-11) {
    font-size: 13px !important;
  }
  .join-card :deep(.text-full-12),
  .join-card :deep(.text-size-12) {
    font-size: 14px !important;
    line-height: 1.25;
  }
  .join-card :deep(.text-size-14),
  .join-card :deep(.text-full-13) {
    font-size: 14px !important;
  }

  .join-card {
    padding: 16px 14px 14px;
    border-radius: 14px;
  }
  .join-steps {
    gap: 8px;
  }
  .join-step--line {
    width: 26px;
  }
  .join-options {
    grid-template-columns: 1fr;
  }
  .join-pill {
    justify-content: flex-start;
    padding: 10px 12px;
  }
  .join-label {
    font-size: 13px;
    letter-spacing: 0;
    text-transform: none;
  }
  .join-req {
    font-size: 14px;
  }
  .join-cards {
    grid-template-columns: 1fr;
  }
  .join-card-option {
    padding: 12px 12px;
  }
  .join-hours-row {
    grid-template-columns: 1fr;
    gap: 6px;
    padding: 10px 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }
  .join-hours-times {
    grid-template-columns: 1fr;
  }
  .join-switch {
    justify-content: flex-start;
  }
  .join-time {
    height: 40px;
  }
  .join-cta {
    height: 44px;
  }
  .join-dropzone-plus,
  .join-work-upload-plus {
    width: 56px;
    height: 56px;
    font-size: 30px;
  }
  .join-work-upload {
    min-height: 110px;
  }
}
</style>

